import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="container mx-auto max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center">Privacy Policy</CardTitle>
            <p className="text-center text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
              <p>
                When you use our WhatsApp Business messaging service, we collect:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Contact information (phone numbers, names) you provide</li>
                <li>Message content and media you send and receive</li>
                <li>Business account information for WhatsApp Business API access</li>
                <li>Usage data and analytics for service improvement</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
              <p>We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide WhatsApp messaging services</li>
                <li>Process and deliver your messages</li>
                <li>Manage your business communications</li>
                <li>Improve our service quality and features</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. WhatsApp Business API Integration</h2>
              <p>
                Our service integrates with WhatsApp Business API. By using our service:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your messages are processed through Meta's WhatsApp Business platform</li>
                <li>Meta's privacy policies and terms also apply to your WhatsApp communications</li>
                <li>We act as a Business Solution Provider under Meta's terms</li>
                <li>Message delivery and storage follow WhatsApp's data handling practices</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Data Sharing and Third Parties</h2>
              <p>We share information with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Meta (WhatsApp) for message delivery and service provision</li>
                <li>Service providers who assist in our operations</li>
                <li>Legal authorities when required by law</li>
              </ul>
              <p>We do not sell your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Security</h2>
              <p>
                We implement appropriate security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption in transit and at rest</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
                <li>Compliance with industry standards</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Data Retention</h2>
              <p>
                We retain your data only as long as necessary for service provision and legal compliance.
                Message history and contact information are retained according to your account settings
                and applicable legal requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. International Transfers</h2>
              <p>
                Your data may be transferred to and processed in countries other than your own.
                We ensure appropriate safeguards are in place for such transfers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
              <p>
                Our service is not intended for children under 13. We do not knowingly collect
                personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. We will notify you of any
                material changes by posting the new policy on our website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Google User Data</h2>
              <p>
                When you connect Google services to WeNext (Google Calendar / Google Meet, YouTube, or Google Ads), we access Google user data only through Google OAuth, only with your explicit authorization, and only to provide the user-facing features described below.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.1 Data Access</h3>
              <p>Depending on which Google integration you connect, WeNext may access the following raw Google user data:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Google Calendar / Google Meet:</strong> Google account email and profile identity used for the connection; calendar metadata; calendar events you authorize WeNext to create, update, or delete for appointments; Google Meet conference links generated as part of those calendar events.
                </li>
                <li>
                  <strong>YouTube:</strong> Google account email and profile identity; YouTube channel identity and metadata; video metadata; comments and community engagement data; YouTube Analytics reports for your connected channel.
                </li>
                <li>
                  <strong>Google Ads:</strong> Google account email and identity used for the connection; Google Ads account and campaign data you authorize WeNext to view or manage (including campaigns, lead forms, conversions, and reporting data).
                </li>
              </ul>
              <p className="mt-2">
                We may also process limited aggregated or anonymized operational metrics derived from the above solely to operate and improve these user-facing features. We do not collect Google data beyond what is required for the connected integration.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.2 Data Use</h3>
              <p>We use Google user data only to provide or improve the following user-facing features:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Google Calendar / Google Meet:</strong> Sync appointments to your Google Calendar; create, update, and delete booking events; generate and attach Google Meet links for scheduled meetings.
                </li>
                <li>
                  <strong>YouTube:</strong> Connect and manage your YouTube channel; sync videos and comments; upload or schedule videos; post, edit, or delete comment replies; display YouTube Analytics in Insights.
                </li>
                <li>
                  <strong>Google Ads:</strong> Connect your Google Ads account; view and manage campaigns, lead forms, conversions, and reporting for accounts you explicitly authorize.
                </li>
              </ul>
              <p className="mt-2">
                Google user data is not used for targeted advertising, lending decisions, or any purpose unrelated to providing or improving these features.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.3 Data Transfer</h3>
              <p>
                Google user data is transmitted to Google APIs to operate the features above. We do not sell Google user data.
              </p>
              <p className="mt-2">We do not transfer or share raw or aggregated/anonymized Google user data with third parties except:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>To Google, as required to call Google APIs for the authorized features;</li>
                <li>To infrastructure processors acting under our instruction (for example, Amazon Web Services) for hosting, storage, and security;</li>
                <li>
                  For YouTube only: when you enable optional AI comment automation, YouTube comment text may be sent to OpenAI solely to generate a personalized reply for your channel. That transfer is used only to provide the feature you enabled and is not used to train generalized AI/ML models.
                </li>
                <li>When required by law or valid legal process.</li>
              </ul>
              <p className="mt-2">
                Google Calendar and Google Ads data are not sent to third-party AI services. We do not transfer Google user data to data brokers or advertisers.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.4 Data Protection</h3>
              <p>We protect Google user data using industry-standard security controls, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption in transit (TLS 1.2/1.3)</li>
                <li>Encryption at rest for OAuth credentials where applied (including AES-256-GCM for YouTube and Google Ads tokens)</li>
                <li>Access limited to authenticated business users within your organization</li>
                <li>Restricted personnel access and periodic access reviews</li>
              </ul>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.5 Data Retention and Deletion</h3>
              <p>
                Google user data associated with an integration is retained only while the integration remains connected and as needed to provide the feature, or as required by law.
              </p>
              <p className="mt-2">
                You may disconnect Google integrations at any time inside WeNext (Scheduling → Integrations for Calendar/Meet; YouTube → Disconnect Channel; Google Ads → Disconnect). Disconnecting removes stored OAuth tokens and connection credentials for that integration. You may also revoke WeNext&apos;s access at{' '}
                <a href="https://myaccount.google.com/permissions" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  https://myaccount.google.com/permissions
                </a>
                .
              </p>
              <p className="mt-2">
                You may also request deletion of Google-related data by contacting support@wenext.today.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.6 Limited Use Restrictions</h3>
              <p>
                <strong>Prohibited data use:</strong> Raw and aggregated/anonymized Google user data is not used for any purpose other than to provide or improve user-facing features described in this policy. Use for targeted advertising, lending, or similar prohibited purposes is not permitted.
              </p>
              <p className="mt-2">
                <strong>Prohibited data transfer:</strong> Raw and aggregated/anonymized Google user data is not transferred or sold to third parties for any purpose other than to provide or improve those user-facing features, subject to the limited processor and legal exceptions above. Transfer or sale to data brokers and advertisers is prohibited.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.7 AI/ML Model Training Restrictions</h3>
              <p>
                WeNext does not use raw or aggregated/anonymized Google user data (including Calendar, YouTube, and Google Ads data) to develop, improve, or train AI/ML models, including generalized or foundation models.
              </p>
              <p className="mt-2">
                We do not transfer Google user data to third-party AI/ML services for the purpose of training their models. Optional YouTube AI comment automation, when enabled by you, is used only to generate personalized replies for your channel and does not use Google data for model training.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">11.8 Limited Use Compliance Statement</h3>
              <p>
                The use of raw or derived user data received from Google APIs (including Google Workspace / Calendar APIs and YouTube APIs) will adhere to the{' '}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-muted p-4 rounded-lg">
                <p><strong>Email:</strong> support@wenext.today</p>
                <p><strong>Address:</strong> Sy. No. 78, 3, Hafeezpet Rd, Bikshapathi Nagar, Hafeezpet, Hyderabad, Telangana 500085</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}